import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * ProjectOverlay - A full-viewport overlay that displays project details
 * in a Konpo.studio-inspired layout with left text panel and right media collage.
 * Features a border drawing animation on open.
 *
 * @param {Object} project - The project data to display (null = closed)
 * @param {Function} onClose - Callback to close the overlay
 */
const ProjectOverlay = ({ project, onClose }) => {
  const { t } = useTranslation();
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [isBorderComplete, setIsBorderComplete] = useState(false);
  const [borderDimensions, setBorderDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const overlayRef = useRef(null);
  const pathRef = useRef(null);

  // Update mobile state on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Calculate border dimensions when overlay opens
  const updateDimensions = useCallback(() => {
    if (overlayRef.current) {
      const rect = overlayRef.current.getBoundingClientRect();

      let width, height;

      if (isMobile) {
        // Mobile: 20px margins on sides, 80px top (navbar), 20px bottom
        width = rect.width - 40; // 20px margin left + 20px margin right
        height = rect.height - 100; // 80px margin top (navbar) + 20px margin bottom
      } else {
        // Desktop: account for sidebar
        const navWidth =
          parseInt(
            getComputedStyle(document.documentElement).getPropertyValue(
              "--nav-width"
            )
          ) || 260;
        width = rect.width - navWidth - 40; // 20px margin on each side after sidebar
        height = rect.height - 40; // 20px margin on top and bottom
      }

      setBorderDimensions({ width, height });
    }
  }, [isMobile]);

  // Prevent body scroll when overlay is open
  useEffect(() => {
    if (project) {
      document.body.style.overflow = "hidden";
      // Reset states when opening
      setIsContentVisible(false);
      setIsBorderComplete(false);

      // Calculate dimensions after a small delay to ensure DOM is ready
      setTimeout(updateDimensions, 10);

      // Start content fade-in slightly before border completes
      const contentTimer = setTimeout(() => {
        setIsContentVisible(true);
      }, 1600); // Start fading in at 1.6s (with 2s border animation)

      // Mark border as complete
      const borderTimer = setTimeout(() => {
        setIsBorderComplete(true);
      }, 2000); // Border animation takes 2s

      // Handle window resize
      window.addEventListener("resize", updateDimensions);

      return () => {
        clearTimeout(contentTimer);
        clearTimeout(borderTimer);
        window.removeEventListener("resize", updateDimensions);
      };
    } else {
      document.body.style.overflow = "";
      setIsContentVisible(false);
      setIsBorderComplete(false);
    }
  }, [project, updateDimensions]);

  // Set up the path animation when dimensions are ready
  useEffect(() => {
    if (
      pathRef.current &&
      borderDimensions.width > 0 &&
      borderDimensions.height > 0
    ) {
      const path = pathRef.current;
      const pathLength = path.getTotalLength();

      // Set up the stroke-dasharray and dashoffset for drawing animation
      path.style.strokeDasharray = pathLength;
      path.style.strokeDashoffset = pathLength;

      // Trigger the animation
      requestAnimationFrame(() => {
        path.style.transition =
          "stroke-dashoffset 2s cubic-bezier(0.4, 0, 0.2, 1)";
        path.style.strokeDashoffset = "0";
      });
    }
  }, [borderDimensions]);

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && project) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [project, onClose]);

  if (!project) return null;

  // Helper function to check if a file is a video
  const isVideoFile = (src) => {
    if (!src) return false;
    const videoExtensions = [".mp4", ".webm", ".ogg", ".mov"];
    return videoExtensions.some((ext) => src.toLowerCase().endsWith(ext));
  };

  // Get images for the collage layout
  const images = project.images || [];
  const mainImage = images[0];
  const secondaryImages = images.slice(1, 5); // Up to 4 secondary images

  // Calculate corner radius and path
  const radius = 20;
  const { width, height } = borderDimensions;

  // Create a rounded rectangle path (starts from top-left, goes clockwise)
  const createRoundedRectPath = (w, h, r) => {
    if (w <= 0 || h <= 0) return "";
    return `
      M ${r} 0
      L ${w - r} 0
      Q ${w} 0 ${w} ${r}
      L ${w} ${h - r}
      Q ${w} ${h} ${w - r} ${h}
      L ${r} ${h}
      Q 0 ${h} 0 ${h - r}
      L 0 ${r}
      Q 0 0 ${r} 0
      Z
    `;
  };

  return (
    <div className="project-overlay-konpo" ref={overlayRef} onClick={onClose}>
      {/* Animated Border SVG */}
      {borderDimensions.width > 0 && borderDimensions.height > 0 && (
        <svg
          className="project-overlay-border-svg"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
        >
          <path
            ref={pathRef}
            className={`project-overlay-border-path ${
              isBorderComplete ? "complete" : ""
            }`}
            d={createRoundedRectPath(width, height, radius)}
          />
        </svg>
      )}

      {/* Back Arrow Button - positioned relative to overlay, not content */}
      <button
        className="project-overlay-konpo-back"
        onClick={onClose}
        aria-label="Go back"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M19 12H5M5 12L12 19M5 12L12 5" />
        </svg>
      </button>

      <div
        className={`project-overlay-konpo-content ${
          isContentVisible ? "visible" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left Panel - Text Content */}
        <div className="project-overlay-konpo-left">
          {/* Category with dot */}
          <div className="project-overlay-konpo-category">
            <span className="project-overlay-konpo-dot"></span>
            <span className="project-overlay-konpo-category-text">
              {project.title}
            </span>
          </div>

          {/* Large Title */}
          <h1 className="project-overlay-konpo-title">{project.subtitle}</h1>

          {/* Description */}
          <p className="project-overlay-konpo-description">
            {project.description}
          </p>

          {/* Meta Info */}
          <div className="project-overlay-konpo-meta">
            <div className="project-overlay-konpo-meta-item">
              <span className="project-overlay-konpo-meta-label">
                {t("work.overlay.categoryLabel")}
              </span>
              <span className="project-overlay-konpo-meta-value">
                {project.category || project.title}
              </span>
            </div>
            <div className="project-overlay-konpo-meta-item">
              <span className="project-overlay-konpo-meta-label">
                {t("work.overlay.statusLabel")}
              </span>
              <span className="project-overlay-konpo-meta-value">
                {t("work.overlay.statusValue")}
              </span>
            </div>
          </div>

          {/* View Project Button */}
          <a
            href={project.link || "#"}
            className="project-overlay-konpo-button"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M7 17L17 7M17 7H7M17 7V17" />
            </svg>
            {t("work.overlay.viewProduct")}
          </a>
        </div>

        {/* Right Panel - Grid Image Collage */}
        <div className="project-overlay-konpo-right">
          <div className="project-overlay-konpo-grid">
            {/* Top Row: Big Video + Small 1 */}
            <div className="project-overlay-konpo-grid-top">
              {/* Main Video Frame */}
              {mainImage && (
                <div className="project-overlay-konpo-video-frame">
                  {isVideoFile(mainImage) ? (
                    <video
                      src={mainImage}
                      alt={`${project.title} - Main`}
                      className="project-overlay-konpo-image"
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                  ) : (
                    <img
                      src={mainImage}
                      alt={`${project.title} - Main`}
                      className="project-overlay-konpo-image"
                    />
                  )}
                </div>
              )}
              {/* Small Image 1 (right of video) */}
              {secondaryImages[0] && (
                <div className="project-overlay-konpo-small-frame">
                  {isVideoFile(secondaryImages[0]) ? (
                    <video
                      src={secondaryImages[0]}
                      alt={`${project.title} - Image 2`}
                      className="project-overlay-konpo-image"
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                  ) : (
                    <img
                      src={secondaryImages[0]}
                      alt={`${project.title} - 2`}
                      className="project-overlay-konpo-image"
                    />
                  )}
                </div>
              )}
            </div>

            {/* Bottom Row: 3 Small Images */}
            <div className="project-overlay-konpo-grid-bottom">
              {secondaryImages.slice(1, 4).map((imgSrc, index) => (
                <div key={index} className="project-overlay-konpo-small-frame">
                  {isVideoFile(imgSrc) ? (
                    <video
                      src={imgSrc}
                      alt={`${project.title} - Image ${index + 3}`}
                      className="project-overlay-konpo-image"
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                  ) : (
                    <img
                      src={imgSrc}
                      alt={`${project.title} - ${index + 3}`}
                      className="project-overlay-konpo-image"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectOverlay;
